/**
 * Extensible client-side keybinding registry (issue #184). Named game actions map
 * to a physical-key binding ({@link KeyBinding}); a single pure {@link matchAction}
 * turns a keyboard event into the action it triggers, so `main.ts` has one dispatch
 * point instead of scattered, hard-coded key checks. Custom bindings persist in
 * localStorage merged over the defaults, so a *new* action added in code inherits
 * its default automatically (and an old persisted table never masks a new action).
 *
 * Kept DOM-free (matchAction takes a plain {@link KeyChord}) so it unit-tests in
 * node like `keymap.ts`. Bindings key off `KeyboardEvent.code` (the layout-
 * independent physical key), consistent with the movement `heldInput` key-set in
 * `main.ts`; the UI renders a friendly label via {@link formatBinding}. Physical
 * binding means an AZERTY player rebinding "the A key" binds the same `KeyA` code
 * a QWERTY player would - by position, not by printed letter.
 */
import { SPHERE_HOTBAR } from '../sim/sphereIcons';

/**
 * The named actions a key can be bound to. Adding a hotkey is: a member here, a
 * default in {@link defaultBindings}, a label in {@link ACTION_LABELS}, and a
 * handler at the single dispatch point in `main.ts` - no change to dispatch wiring.
 */
export type Action =
  | 'all-names'
  | 'war-mode'
  | 'say-last'
  | 'bow'
  | 'salute'
  | 'dances'
  | 'fire-bow'
  | 'moonwalk'
  | 'wardrobe'
  | 'sphere-slot-1'
  | 'sphere-slot-2'
  | 'sphere-slot-3'
  | 'sphere-slot-4'
  | 'sphere-slot-5'
  | 'sphere-slot-6'
  | 'sphere-slot-7'
  | 'sphere-slot-8'
  | 'admin-teleport';

/**
 * The Sphere Duel hotbar's slot actions in slot order (one per {@link SPHERE_HOTBAR}
 * entry): the hud renders each slot's badge from its binding and the sphere
 * installer registers one handler per slot, both indexing this tuple by slot.
 */
export const SPHERE_SLOT_ACTIONS = [
  'sphere-slot-1',
  'sphere-slot-2',
  'sphere-slot-3',
  'sphere-slot-4',
  'sphere-slot-5',
  'sphere-slot-6',
  'sphere-slot-7',
  'sphere-slot-8',
] as const satisfies readonly Action[];

/** The actions in the order the rebinding panel lists them. */
export const ACTIONS: readonly Action[] = [
  'all-names',
  'war-mode',
  'say-last',
  'bow',
  'salute',
  'dances',
  'fire-bow',
  'moonwalk',
  'wardrobe',
  ...SPHERE_SLOT_ACTIONS,
  'admin-teleport',
];

/**
 * Actions that are **admin-only** (ADR-0183): the rebinding panel hides their rows from
 * non-admins (cosmetic - the action's `main.ts` handler and the server both gate the
 * power regardless), so admin features aren't advertised. The action still exists in
 * {@link ACTIONS} / {@link matchAction} for the admin who has it bound.
 */
export const ADMIN_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  'dances',
  'admin-teleport',
]);

/**
 * A sphere slot's rebinding-panel label, derived from {@link SPHERE_HOTBAR} so the
 * panel always names the spell/potion actually on that slot - the hotbar content is
 * the sim's data, not this module's.
 */
const sphereSlotLabel = (slot: number): string =>
  `Sphere duel: ${SPHERE_HOTBAR[slot]?.label ?? `slot ${slot + 1}`}`;

/** Human-readable label per action, shown in the rebinding panel. */
export const ACTION_LABELS: Record<Action, string> = {
  'all-names': 'Show all names',
  'war-mode': 'Toggle war mode',
  'say-last': 'Say last message',
  bow: 'Bow',
  salute: 'Salute',
  dances: 'Dance',
  'fire-bow': 'Fire bow (duel)',
  moonwalk: 'Moonwalk',
  wardrobe: 'Toggle wardrobe',
  'sphere-slot-1': sphereSlotLabel(0),
  'sphere-slot-2': sphereSlotLabel(1),
  'sphere-slot-3': sphereSlotLabel(2),
  'sphere-slot-4': sphereSlotLabel(3),
  'sphere-slot-5': sphereSlotLabel(4),
  'sphere-slot-6': sphereSlotLabel(5),
  'sphere-slot-7': sphereSlotLabel(6),
  'sphere-slot-8': sphereSlotLabel(7),
  'admin-teleport': 'Admin teleport',
};

/**
 * A physical-key binding: a `KeyboardEvent.code` plus the modifiers that must be
 * held with it. A modifier left unset (or `false`) must be *absent* for the binding
 * to match, so `Alt+F` fires only on Alt+F, never on Ctrl+Alt+F.
 */
export interface KeyBinding {
  code: string;
  alt?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
}

/** A full action -> binding table. */
export type BindingTable = Record<Action, KeyBinding>;

/**
 * The minimal shape of a keyboard event {@link matchAction} needs. A real
 * `KeyboardEvent` satisfies it structurally, and a test can pass a plain object -
 * keeping this module DOM-free and node-testable.
 */
export interface KeyChord {
  code: string;
  altKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
}

/** localStorage key custom bindings persist under (mirrors `main.ts`'s `britbank.*` keys). */
export const KEYBINDINGS_KEY = 'britbank.keybindings';

/**
 * `KeyboardEvent.code`s that are pure modifiers - never a binding on their own and
 * skipped while capturing a rebind (the UI waits for a "real" key).
 */
const MODIFIER_CODES = new Set([
  'AltLeft', 'AltRight',
  'ControlLeft', 'ControlRight',
  'ShiftLeft', 'ShiftRight',
  'MetaLeft', 'MetaRight',
]);

/**
 * Keys reserved from rebinding so a hotkey can never silently shadow another input.
 * Two groups: chat/splash text entry (`chat.ts` uses Enter/Escape), and the
 * movement/run keys (WASD + arrows + Shift) that `main.ts` steers the character with
 * - those are not registry actions, so {@link conflictingActions} can't catch a clash
 * against them; refusing them here keeps the rebind UI from binding one and quietly
 * stopping the character from walking. Keep in sync with `main.ts`'s MOVEMENT_KEYS /
 * RUN_KEYS.
 */
const RESERVED_CODES = new Set([
  'Escape', 'Enter', 'NumpadEnter',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'ShiftLeft', 'ShiftRight',
]);

/** Whether `code` is a pure modifier key (Alt/Ctrl/Shift/Meta). */
export function isModifierCode(code: string): boolean {
  return MODIFIER_CODES.has(code);
}

/**
 * The modifier state of a chord, for deciding whether a reserved movement/chat key is
 * actually shadowed. A subset of {@link KeyChord}, so a real `KeyboardEvent` or a plain
 * test object satisfies it structurally.
 */
export interface ChordModifiers {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}

/**
 * Whether `code` is reserved from rebinding (chat's Enter/Escape or a movement/run key)
 * so a hotkey can never silently shadow another input.
 *
 * The reservation is **only meaningful for a bare (or Shift-only) press**: the
 * movement/run and chat keys are all consumed unmodified in `main.ts` (WASD walks, bare
 * Enter sends chat), and the global hotkey dispatch matches a bound chord *before* the
 * movement check and returns, so a **Ctrl/Alt/Meta-modified** chord on a reserved key
 * (e.g. `Ctrl+S`, the Salute emote default) can never shadow bare movement - `KeyS`
 * alone still walks south. So when `modifiers` reports a Ctrl/Alt/Meta chord, the code is
 * *not* reserved and the emote binding is allowed; Shift alone does **not** unreserve it
 * (Shift is itself the run modifier). Called with no `modifiers` (the default) the check
 * is the strict, bare-press reservation, so any caller that only knows the code stays on
 * the safe side.
 */
export function isReservedCode(code: string, modifiers?: ChordModifiers): boolean {
  if (!RESERVED_CODES.has(code)) return false;
  const modified = !!modifiers && (modifiers.ctrlKey || modifiers.altKey || modifiers.metaKey);
  return !modified;
}

/**
 * The built-in default binding for every action. Alt+F reveals all names (issue
 * #184); a bare Tab toggles war mode (Rat Swarm Slice E, PRD #222 - the classic
 * UO war-mode key; not in {@link RESERVED_CODES}, so it is rebindable, and the
 * dispatch's `preventDefault` keeps the browser's Tab focus-walk off the game).
 * Ctrl+Q recalls the player's last sent chat line into the input ("say last") -
 * `KeyQ` is not reserved, so it too rebinds, and the dispatch's `preventDefault`
 * keeps the browser's own Ctrl+Q (quit, on some browsers) from firing.
 *
 * The emotes default to the classic modifier gestures: Ctrl+B bows (action 32), Ctrl+S
 * salutes (action 33), and Ctrl+D dances (action 30, ADR-0387). Dance is temporarily
 * admin-only, so its Settings row and client handler are hidden for non-admins and the
 * server re-authorizes it. `KeyB` is not reserved; `KeyS` and `KeyD` are bare movement
 * keys, but the Ctrl modifier lifts the reservation ({@link isReservedCode}). A
 * Ctrl-modified chord can never shadow bare movement. The dispatch's `preventDefault`
 * suppresses the browser's Ctrl+B, Ctrl+S, and Ctrl+D actions.
 *
 * Moonwalk (ADR-0145) defaults to Ctrl+M - M for moonwalk. `KeyM` is not reserved, and
 * Ctrl+M is not a common browser shortcut; the dispatch's `preventDefault` on a matched
 * chord suppresses any default (e.g. Firefox's collapse-menu) so triggering the glide
 * never fires a page side effect.
 *
 * Admin teleport (ADR-0183) defaults to a bare `KeyT` (captain's choice) - `KeyT` is
 * not a reserved movement/chat key and is unused by any other default, so it never
 * collides; the whole hotkey dispatch is behind `isTextEntryFocused`, so a bare letter
 * never fires while typing in chat. The action + its handler are admin-gated, so a
 * non-admin who somehow has it bound just gets nothing, and the server rejects the
 * command regardless.
 *
 * Fire bow (ADR-0255) defaults to a bare `KeyF`. It does **not** collide with the
 * `Alt+F` all-names binding: `chordMatchesBinding` requires an exact modifier match, so
 * bare `KeyF` and `Alt+F` are distinct chords. `KeyF` is not a reserved movement/chat key
 * and, like every hotkey, only fires while not typing (`isTextEntryFocused`); the handler
 * itself only sends a command while in a duel, so a stray F otherwise does nothing.
 *
 * Wardrobe (ADR-0285) defaults to Alt+P and toggles the same full gump as the toolbar
 * button and `/dress`. It is an ordinary registry action, so Settings exposes it for
 * rebinding and old persisted tables inherit this default through merge-over-defaults.
 *
 * The Sphere Duel hotbar slots (settings-overhaul) default to the bare digit row,
 * Digit1-Digit8 in slot order - the keys the hotbar hardwired before they became
 * registry actions. Digits are not reserved movement/chat keys, and like every
 * hotkey they only fire while not typing; the per-slot handlers (installSphere.ts)
 * additionally no-op unless the hotbar is visible and the slot is castable, so a
 * stray digit outside a duel does nothing.
 */
export function defaultBindings(): BindingTable {
  return {
    'all-names': { code: 'KeyF', alt: true },
    'war-mode': { code: 'Tab' },
    'say-last': { code: 'KeyQ', ctrl: true },
    bow: { code: 'KeyB', ctrl: true },
    salute: { code: 'KeyS', ctrl: true },
    dances: { code: 'KeyD', ctrl: true },
    'fire-bow': { code: 'KeyF' },
    moonwalk: { code: 'KeyM', ctrl: true },
    wardrobe: { code: 'KeyP', alt: true },
    'sphere-slot-1': { code: 'Digit1' },
    'sphere-slot-2': { code: 'Digit2' },
    'sphere-slot-3': { code: 'Digit3' },
    'sphere-slot-4': { code: 'Digit4' },
    'sphere-slot-5': { code: 'Digit5' },
    'sphere-slot-6': { code: 'Digit6' },
    'sphere-slot-7': { code: 'Digit7' },
    'sphere-slot-8': { code: 'Digit8' },
    'admin-teleport': { code: 'KeyT' },
  };
}

/** Whether a chord's modifiers exactly match a binding's (an unset modifier must be absent). */
function chordMatchesBinding(chord: KeyChord, binding: KeyBinding): boolean {
  return (
    chord.code === binding.code &&
    chord.altKey === !!binding.alt &&
    chord.ctrlKey === !!binding.ctrl &&
    chord.shiftKey === !!binding.shift &&
    chord.metaKey === !!binding.meta
  );
}

/**
 * The action a keyboard chord triggers, or `null` for none. Pure: the single
 * dispatch point in `main.ts` calls this after its typing guard, so a matched
 * action never fires while the player is typing. On a conflict (two actions on one
 * key) the first in {@link ACTIONS} wins - the rebind UI surfaces such conflicts so
 * they don't stay silently shadowed.
 */
export function matchAction(chord: KeyChord, bindings: BindingTable): Action | null {
  for (const action of ACTIONS) {
    const binding = bindings[action];
    if (binding && chordMatchesBinding(chord, binding)) return action;
  }
  return null;
}

/** Whether two bindings are identical (same physical key + same modifiers). */
export function bindingsEqual(a: KeyBinding, b: KeyBinding): boolean {
  return (
    a.code === b.code &&
    !!a.alt === !!b.alt &&
    !!a.ctrl === !!b.ctrl &&
    !!a.shift === !!b.shift &&
    !!a.meta === !!b.meta
  );
}

/**
 * The set of actions that share their binding with at least one other action - a
 * conflict the rebind UI surfaces rather than silently letting {@link matchAction}
 * shadow one. Empty when every binding is unique.
 *
 * `actions` scopes which actions participate in conflict detection (defaults to every
 * {@link ACTIONS}). The Settings panel passes only the *visible* rows for a non-admin
 * (ADR-0183) so a visible key rebound onto the hidden `admin-teleport` default (bare
 * `KeyT`) is never flagged against a row the user cannot see or resolve.
 */
export function conflictingActions(
  bindings: BindingTable,
  actions: readonly Action[] = ACTIONS,
): Set<Action> {
  const conflicts = new Set<Action>();
  for (const a of actions) {
    for (const b of actions) {
      if (a === b) continue;
      if (bindingsEqual(bindings[a], bindings[b])) {
        conflicts.add(a);
        conflicts.add(b);
      }
    }
  }
  return conflicts;
}

/** Turn a `KeyboardEvent.code` into a short human label (`KeyF` -> `F`, `ArrowUp` -> `↑`). */
export function formatCode(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  switch (code) {
    case 'ArrowUp': return '↑';
    case 'ArrowDown': return '↓';
    case 'ArrowLeft': return '←';
    case 'ArrowRight': return '→';
    case 'Space': return 'Space';
    default: return code;
  }
}

/** A friendly, one-line label for a binding, e.g. `Alt+F` or `Ctrl+Shift+K`. */
export function formatBinding(binding: KeyBinding): string {
  const parts: string[] = [];
  if (binding.ctrl) parts.push('Ctrl');
  if (binding.alt) parts.push('Alt');
  if (binding.shift) parts.push('Shift');
  if (binding.meta) parts.push('Meta');
  parts.push(formatCode(binding.code));
  return parts.join('+');
}

/** A structural storage (the browser `localStorage`, or a fake in tests). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The ambient `localStorage`, or `null` when it is absent/blocked (private mode, node). */
function ambientStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** Whether a parsed value is a usable binding (an object with a string `code`). */
function isValidBinding(value: unknown): value is KeyBinding {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string'
  );
}

/** Copy a binding down to just its known fields, with modifiers as `true`-or-absent. */
function normalizeBinding(binding: KeyBinding): KeyBinding {
  const out: KeyBinding = { code: binding.code };
  if (binding.alt) out.alt = true;
  if (binding.ctrl) out.ctrl = true;
  if (binding.shift) out.shift = true;
  if (binding.meta) out.meta = true;
  return out;
}

/**
 * The persisted bindings merged over {@link defaultBindings}, so a stored table
 * missing a newly-added action still gets that action's default, and a malformed or
 * unknown entry falls back cleanly. Reads the ambient `localStorage` unless a
 * {@link StorageLike} is injected (tests). Any failure yields the defaults.
 */
export function loadBindings(storage?: StorageLike): BindingTable {
  const defaults = defaultBindings();
  const store = storage ?? ambientStorage();
  if (!store) return defaults;
  try {
    const raw = store.getItem(KEYBINDINGS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<Record<Action, unknown>>;
    const merged = { ...defaults };
    for (const action of ACTIONS) {
      const candidate = parsed?.[action];
      if (isValidBinding(candidate)) merged[action] = normalizeBinding(candidate);
    }
    return merged;
  } catch {
    return defaults;
  }
}

/** Persist the binding table so custom bindings survive a reload. Silent if storage is unavailable. */
export function saveBindings(bindings: BindingTable, storage?: StorageLike): void {
  const store = storage ?? ambientStorage();
  if (!store) return;
  try {
    store.setItem(KEYBINDINGS_KEY, JSON.stringify(bindings));
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}
